import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Badge,
  IconButton,
  InputBase,
  Paper,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tab,
  Tabs,
  TextField,
  Chip,
  CircularProgress,
  Tooltip,
  Autocomplete,
  alpha,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Send as SendIcon,
  FiberManualRecord as OnlineDotIcon,
  Tag as TagIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  People as PeopleIcon,
  PersonAddAlt1 as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
  AutoAwesome as AutoBadgeIcon,
} from '@mui/icons-material';
import { useSocket } from '../contexts/SocketContext';
import { useAuth } from '../contexts/AuthContext';
import {
  getChannels,
  getMessages,
  sendMessage,
  createChannel,
  createDM,
  markRead,
  addChannelMembers,
  removeChannelMember,
  getChannelMembers,
  getMessagingUsers,
} from '../services/api';
import { formatDistanceToNow, format, isToday, isYesterday } from 'date-fns';

// ─── Markdown-lite renderer ──────────────────────────────────────────────────
function renderContent(text) {
  if (!text) return '';
  // Bold: **text**
  let html = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  // Italic: *text* or _text_
  html = html.replace(/(?<!\*)\*(?!\*)(.*?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');
  html = html.replace(/_(.*?)_/g, '<em>$1</em>');
  // Code: `text`
  html = html.replace(/`(.*?)`/g, '<code style="background:#f0f0f0;padding:2px 4px;border-radius:3px;font-family:monospace;font-size:0.85em">$1</code>');
  // Newlines
  html = html.replace(/\n/g, '<br/>');
  return html;
}

// ─── Time formatting ──────────────────────────────────────────────────────────
function formatMsgTime(dateStr) {
  const d = new Date(dateStr);
  if (isToday(d)) return format(d, 'h:mm a');
  if (isYesterday(d)) return `Yesterday ${format(d, 'h:mm a')}`;
  return format(d, 'MMM d, h:mm a');
}

function formatChannelTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isToday(d)) return format(d, 'h:mm a');
  if (isYesterday(d)) return 'Yesterday';
  return format(d, 'MMM d');
}

// ─── Avatar helper ────────────────────────────────────────────────────────────
function UserAvatar({ name, size = 36 }) {
  const initials = name
    ? name.split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase()
    : '?';
  const colors = ['#1A5490', '#1565c0', '#2e7d32', '#6a1b9a', '#c62828', '#e65100', '#00695c'];
  const colorIndex = name
    ? name.charCodeAt(0) % colors.length
    : 0;
  return (
    <Avatar sx={{ width: size, height: size, bgcolor: colors[colorIndex], fontSize: size * 0.38 }}>
      {initials}
    </Avatar>
  );
}

// ─── Channel sidebar item ─────────────────────────────────────────────────────
function ChannelItem({ channel, active, onSelect, onlineUsers }) {
  const displayName = channel.type === 'dm'
    ? (channel.dm_user_name || 'Direct Message')
    : channel.name;

  const isOnline = channel.type === 'dm' && channel.dm_user_id && onlineUsers.has(channel.dm_user_id);
  const unread = parseInt(channel.unread_count) || 0;

  return (
    <ListItem disablePadding>
      <ListItemButton
        selected={active}
        onClick={() => onSelect(channel)}
        sx={{
          py: 0.75,
          px: 1.5,
          '&.Mui-selected': { bgcolor: alpha('#1A5490', 0.12) },
          '&.Mui-selected:hover': { bgcolor: alpha('#1A5490', 0.16) },
        }}
      >
        <ListItemAvatar sx={{ minWidth: 44 }}>
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={
              isOnline ? (
                <OnlineDotIcon sx={{ fontSize: 10, color: '#2e7d32' }} />
              ) : null
            }
          >
            {channel.type === 'dm' ? (
              <UserAvatar name={displayName} size={36} />
            ) : (
              <Avatar sx={{ width: 36, height: 36, bgcolor: '#e3f2fd', color: '#1A5490' }}>
                {channel.type === 'group' ? <GroupIcon fontSize="small" /> : <TagIcon fontSize="small" />}
              </Avatar>
            )}
          </Badge>
        </ListItemAvatar>
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography
                variant="body2"
                fontWeight={unread > 0 ? 700 : 500}
                noWrap
                sx={{ maxWidth: 140 }}
              >
                {displayName}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', ml: 1 }}>
                {formatChannelTime(channel.last_message_at)}
              </Typography>
            </Box>
          }
          secondary={
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography
                variant="caption"
                color={unread > 0 ? 'text.primary' : 'text.secondary'}
                fontWeight={unread > 0 ? 600 : 400}
                noWrap
                sx={{ maxWidth: 150 }}
              >
                {channel.last_message_by && channel.last_message
                  ? `${channel.last_message_by.split(' ')[0]}: ${channel.last_message}`
                  : channel.last_message || 'No messages yet'}
              </Typography>
              {unread > 0 && (
                <Chip
                  label={unread > 99 ? '99+' : unread}
                  size="small"
                  color="error"
                  sx={{ height: 18, fontSize: '0.65rem', minWidth: 22 }}
                />
              )}
            </Box>
          }
        />
      </ListItemButton>
    </ListItem>
  );
}

// ─── Message bubble ───────────────────────────────────────────────────────────
function MessageBubble({ message, prevMessage, currentUserId }) {
  const isMine = message.user_id === currentUserId;
  const showHeader =
    !prevMessage ||
    prevMessage.user_id !== message.user_id ||
    new Date(message.created_at) - new Date(prevMessage.created_at) > 5 * 60 * 1000;

  return (
    <Box sx={{ mb: showHeader ? 1.5 : 0.25, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      {/* Avatar column — always present for layout, hidden when grouping */}
      <Box sx={{ width: 36, flexShrink: 0, mt: showHeader ? 0 : 0 }}>
        {showHeader && <UserAvatar name={message.user_name} size={36} />}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {showHeader && (
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.25 }}>
            <Typography variant="body2" fontWeight={700} color={isMine ? '#1A5490' : 'text.primary'}>
              {isMine ? 'You' : message.user_name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatMsgTime(message.created_at)}
            </Typography>
          </Box>
        )}
        <Typography
          variant="body2"
          component="div"
          sx={{ lineHeight: 1.5, wordBreak: 'break-word' }}
          dangerouslySetInnerHTML={{ __html: renderContent(message.content) }}
        />
      </Box>
    </Box>
  );
}

// ─── New Channel / DM Modal ───────────────────────────────────────────────────
function NewChannelModal({ open, onClose, onChannelCreated }) {
  const [tab, setTab] = useState(0);
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [loading, setLoading] = useState(false);
  const { user: currentUser } = useAuth();

  useEffect(() => {
    if (open) {
      getMessagingUsers()
        .then((res) => setUsers(res.data))
        .catch(console.error);
      setSelectedUsers([]);
      setGroupName('');
      setUserSearch('');
      setTab(0);
    }
  }, [open]);

  const filteredUsers = users.filter((u) =>
    u.name.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email.toLowerCase().includes(userSearch.toLowerCase())
  );

  const toggleUser = (u) => {
    setSelectedUsers((prev) =>
      prev.find((x) => x.id === u.id)
        ? prev.filter((x) => x.id !== u.id)
        : [...prev, u]
    );
  };

  const handleCreateDM = async (u) => {
    try {
      setLoading(true);
      const res = await createDM(u.id);
      onChannelCreated(res.data);
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim() || selectedUsers.length === 0) return;
    try {
      setLoading(true);
      const res = await createChannel({
        name: groupName.trim(),
        member_ids: selectedUsers.map((u) => u.id),
      });
      onChannelCreated(res.data);
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 0 }}>
        New Message
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2 }}>
        <Tab label="Direct Message" />
        <Tab label="New Group" />
      </Tabs>
      <DialogContent sx={{ pt: 1 }}>
        {tab === 0 && (
          <>
            <TextField
              fullWidth
              size="small"
              placeholder="Search people..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              sx={{ mb: 1 }}
            />
            <List dense sx={{ maxHeight: 320, overflow: 'auto' }}>
              {filteredUsers.map((u) => (
                <ListItem key={u.id} disablePadding>
                  <ListItemButton onClick={() => handleCreateDM(u)} disabled={loading}>
                    <ListItemAvatar>
                      <UserAvatar name={u.name} size={32} />
                    </ListItemAvatar>
                    <ListItemText
                      primary={u.name}
                      secondary={u.role}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </>
        )}
        {tab === 1 && (
          <>
            <TextField
              fullWidth
              size="small"
              label="Group name"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              sx={{ mb: 1.5 }}
            />
            {selectedUsers.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                {selectedUsers.map((u) => (
                  <Chip
                    key={u.id}
                    label={u.name.split(' ')[0]}
                    size="small"
                    onDelete={() => toggleUser(u)}
                  />
                ))}
              </Box>
            )}
            <TextField
              fullWidth
              size="small"
              placeholder="Search people..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              sx={{ mb: 1 }}
            />
            <List dense sx={{ maxHeight: 240, overflow: 'auto' }}>
              {filteredUsers.map((u) => {
                const selected = selectedUsers.some((x) => x.id === u.id);
                return (
                  <ListItem key={u.id} disablePadding>
                    <ListItemButton onClick={() => toggleUser(u)} selected={selected}>
                      <ListItemAvatar>
                        <UserAvatar name={u.name} size={32} />
                      </ListItemAvatar>
                      <ListItemText
                        primary={u.name}
                        secondary={u.role}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </>
        )}
      </DialogContent>
      {tab === 1 && (
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateGroup}
            disabled={loading || !groupName.trim() || selectedUsers.length === 0}
          >
            {loading ? <CircularProgress size={18} /> : 'Create Group'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}

// ─── Main Messaging Component ─────────────────────────────────────────────────
export default function Messaging() {
  const { user: currentUser } = useAuth();
  const socket = useSocket();

  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [channelSearch, setChannelSearch] = useState('');
  const [compose, setCompose] = useState('');
  const [sending, setSending] = useState(false);
  const [typingUsers, setTypingUsers] = useState({}); // { channelId: [names] }
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [newChannelOpen, setNewChannelOpen] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(true);

  // Members panel state (Tom Clancy, 2026-04-25 — Option C membership control).
  const [membersOpen, setMembersOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [addPickerValue, setAddPickerValue] = useState(null);
  const [memberMutating, setMemberMutating] = useState(false);

  const messagesEndRef = useRef(null);
  const composeRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const activeChannelRef = useRef(null);

  // Keep ref in sync for socket callbacks
  useEffect(() => {
    activeChannelRef.current = activeChannel?.id;
  }, [activeChannel]);

  // ── Load channels ───────────────────────────────────────────────────────────
  const loadChannels = useCallback(async () => {
    try {
      const res = await getChannels();
      setChannels(res.data);
    } catch (err) {
      console.error('loadChannels error:', err);
    }
  }, []);

  useEffect(() => {
    loadChannels();
  }, [loadChannels]);

  // ── Load messages for active channel ───────────────────────────────────────
  useEffect(() => {
    if (!activeChannel) return;
    setMessages([]);
    setHasMore(false);
    setMessagesLoading(true);

    getMessages(activeChannel.id)
      .then((res) => {
        setMessages(res.data.messages || []);
        setHasMore(res.data.has_more || false);
      })
      .catch(console.error)
      .finally(() => setMessagesLoading(false));
  }, [activeChannel?.id]);

  // ── Auto-scroll to bottom on new messages ──────────────────────────────────
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // ── Socket.IO events ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!socket) return;

    const handleNewMessage = (msg) => {
      if (msg.channel_id === activeChannelRef.current) {
        setMessages((prev) => [...prev, msg]);
        // Mark read
        markRead(msg.channel_id).catch(() => {});
      }
      // Refresh channel list to update unread counts + last message
      loadChannels();
    };

    const handleUserOnline = ({ userId }) => {
      setOnlineUsers((prev) => new Set([...prev, userId]));
    };

    const handleUserOffline = ({ userId }) => {
      setOnlineUsers((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    };

    const handleUserTyping = ({ channelId, userId, name }) => {
      setTypingUsers((prev) => {
        const names = prev[channelId] || [];
        if (names.includes(name)) return prev;
        return { ...prev, [channelId]: [...names, name] };
      });
    };

    const handleStopTyping = ({ channelId, userId }) => {
      setTypingUsers((prev) => {
        const names = (prev[channelId] || []).filter((_, i) => i !== 0); // rough cleanup
        return { ...prev, [channelId]: names };
      });
    };

    socket.on('new_message', handleNewMessage);
    socket.on('user_online', handleUserOnline);
    socket.on('user_offline', handleUserOffline);
    socket.on('user_typing', handleUserTyping);
    socket.on('user_stop_typing', handleStopTyping);

    return () => {
      socket.off('new_message', handleNewMessage);
      socket.off('user_online', handleUserOnline);
      socket.off('user_offline', handleUserOffline);
      socket.off('user_typing', handleUserTyping);
      socket.off('user_stop_typing', handleStopTyping);
    };
  }, [socket, loadChannels]);

  // ── Send message ────────────────────────────────────────────────────────────
  const handleSend = async () => {
    if (!compose.trim() || !activeChannel || sending) return;
    const text = compose.trim();
    setCompose('');
    setSending(true);
    try {
      await sendMessage(activeChannel.id, text);
      // Message will come back via socket; also refresh channels
      loadChannels();
    } catch (err) {
      console.error('Send error:', err);
      setCompose(text); // restore on error
    } finally {
      setSending(false);
      composeRef.current?.focus();
    }
  };

  // ── Typing indicator ────────────────────────────────────────────────────────
  const handleComposeChange = (e) => {
    setCompose(e.target.value);
    if (!socket || !activeChannel) return;
    socket.emit('typing', { channelId: activeChannel.id });
    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      socket.emit('stop_typing', { channelId: activeChannel.id });
    }, 2000);
  };

  const handleComposeKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ── Load more (older messages) ──────────────────────────────────────────────
  const handleLoadMore = async () => {
    if (!hasMore || messages.length === 0) return;
    const oldest = messages[0];
    const res = await getMessages(activeChannel.id, { before: oldest.id });
    setMessages((prev) => [...(res.data.messages || []), ...prev]);
    setHasMore(res.data.has_more || false);
  };

  // ── Channel selected ────────────────────────────────────────────────────────
  // Members panel handlers (Tom Clancy, 2026-04-25 — Option C).
  const openMembersPanel = async () => {
    if (!activeChannel) return;
    setMembersOpen(true);
    setMembersLoading(true);
    try {
      const [m, u] = await Promise.all([
        getChannelMembers(activeChannel.id),
        // Reuse the messaging-users list (active employees) for the picker.
        allUsers.length > 0 ? Promise.resolve({ data: allUsers }) : getMessagingUsers(),
      ]);
      setMembers(m.data || []);
      if (allUsers.length === 0) setAllUsers(u.data || []);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Members load failed:', e);
    } finally {
      setMembersLoading(false);
    }
  };

  const handleAddMember = async () => {
    if (!addPickerValue || !activeChannel) return;
    setMemberMutating(true);
    try {
      await addChannelMembers(activeChannel.id, [addPickerValue.id]);
      const m = await getChannelMembers(activeChannel.id);
      setMembers(m.data || []);
      setAddPickerValue(null);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Add member failed:', e);
    } finally {
      setMemberMutating(false);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!activeChannel) return;
    setMemberMutating(true);
    try {
      await removeChannelMember(activeChannel.id, userId);
      const m = await getChannelMembers(activeChannel.id);
      setMembers(m.data || []);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Remove member failed:', e);
    } finally {
      setMemberMutating(false);
    }
  };

  const handleSelectChannel = (channel) => {
    setActiveChannel(channel);
    setMobileSidebarOpen(false);
    // Socket: join the channel room in case it was newly created
    if (socket) socket.emit('join_channel', { channelId: channel.id });
  };

  // ── Channel created (from modal) ────────────────────────────────────────────
  const handleChannelCreated = (newChannel) => {
    loadChannels();
    // Reload again shortly so DM channel gets dm_user fields populated
    setTimeout(() => {
      loadChannels();
    }, 300);
  };

  // ── Grouped channels for sidebar ────────────────────────────────────────────
  const searchLower = channelSearch.toLowerCase();
  const filteredChannels = channels.filter((c) => {
    const name = c.type === 'dm' ? (c.dm_user_name || '') : (c.name || '');
    return name.toLowerCase().includes(searchLower);
  });

  const dmChannels = filteredChannels.filter((c) => c.type === 'dm');
  const projectChannels = filteredChannels.filter((c) => c.type === 'project');
  const groupChannels = filteredChannels.filter((c) => c.type === 'group');

  const totalUnread = channels.reduce((sum, c) => sum + (parseInt(c.unread_count) || 0), 0);

  // ── Typing label for active channel ────────────────────────────────────────
  const typingNames = activeChannel ? (typingUsers[activeChannel.id] || []) : [];
  const typingLabel = typingNames.length === 1
    ? `${typingNames[0]} is typing…`
    : typingNames.length > 1
      ? `${typingNames.join(', ')} are typing…`
      : '';

  // ── Active channel display name ─────────────────────────────────────────────
  const activeDisplayName = activeChannel
    ? activeChannel.type === 'dm'
      ? (activeChannel.dm_user_name || 'Direct Message')
      : activeChannel.name
    : '';

  // ── Sidebar ─────────────────────────────────────────────────────────────────
  const sidebar = (
    <Box
      sx={{
        width: 280,
        flexShrink: 0,
        height: '100%',
        borderRight: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
      }}
    >
      {/* Header */}
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="h6" fontWeight={700}>
            Messages
            {totalUnread > 0 && (
              <Chip
                label={totalUnread > 99 ? '99+' : totalUnread}
                size="small"
                color="error"
                sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
              />
            )}
          </Typography>
          <Tooltip title="New Message">
            <IconButton size="small" onClick={() => setNewChannelOpen(true)} color="primary">
              <AddIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Search */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            bgcolor: alpha('#000', 0.05),
            borderRadius: 1,
            px: 1,
          }}
        >
          <SearchIcon fontSize="small" sx={{ color: 'text.secondary', mr: 0.5 }} />
          <InputBase
            fullWidth
            size="small"
            placeholder="Search channels…"
            value={channelSearch}
            onChange={(e) => setChannelSearch(e.target.value)}
            sx={{ fontSize: '0.875rem' }}
          />
        </Box>
      </Box>

      {/* Channel list */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {dmChannels.length > 0 && (
          <>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ px: 2, py: 0.5, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Direct Messages
            </Typography>
            <List dense disablePadding>
              {dmChannels.map((c) => (
                <ChannelItem
                  key={c.id}
                  channel={c}
                  active={activeChannel?.id === c.id}
                  onSelect={handleSelectChannel}
                  onlineUsers={onlineUsers}
                />
              ))}
            </List>
          </>
        )}

        {projectChannels.length > 0 && (
          <>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ px: 2, py: 0.5, mt: 1, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Project Channels
            </Typography>
            <List dense disablePadding>
              {projectChannels.map((c) => (
                <ChannelItem
                  key={c.id}
                  channel={c}
                  active={activeChannel?.id === c.id}
                  onSelect={handleSelectChannel}
                  onlineUsers={onlineUsers}
                />
              ))}
            </List>
          </>
        )}

        {groupChannels.length > 0 && (
          <>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ px: 2, py: 0.5, mt: 1, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Groups
            </Typography>
            <List dense disablePadding>
              {groupChannels.map((c) => (
                <ChannelItem
                  key={c.id}
                  channel={c}
                  active={activeChannel?.id === c.id}
                  onSelect={handleSelectChannel}
                  onlineUsers={onlineUsers}
                />
              ))}
            </List>
          </>
        )}

        {filteredChannels.length === 0 && (
          <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {channelSearch ? 'No channels match your search.' : 'No channels yet. Start a conversation!'}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  // ── Main panel ──────────────────────────────────────────────────────────────
  const mainPanel = (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}>
      {activeChannel ? (
        <>
          {/* Channel header */}
          <Box
            sx={{
              px: 3,
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {/* Mobile back button */}
            <IconButton
              size="small"
              onClick={() => setMobileSidebarOpen(true)}
              sx={{ display: { sm: 'none' }, mr: 0.5 }}
            >
              <ArrowBackIcon />
            </IconButton>

            {activeChannel.type === 'dm' ? (
              <PersonIcon sx={{ color: 'text.secondary' }} />
            ) : activeChannel.type === 'group' ? (
              <GroupIcon sx={{ color: 'text.secondary' }} />
            ) : (
              <TagIcon sx={{ color: 'text.secondary' }} />
            )}
            <Box>
              <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
                {activeDisplayName}
              </Typography>
              {activeChannel.description && (
                <Typography variant="caption" color="text.secondary">
                  {activeChannel.description}
                </Typography>
              )}
            </Box>

            <Box sx={{ flex: 1 }} />

            {/* Members button — visible on every channel except DMs.
                Tom Clancy, 2026-04-25: any channel member can add or remove. */}
            {activeChannel.type !== 'dm' && (
              <Tooltip title="Manage members">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PeopleIcon />}
                  onClick={openMembersPanel}
                  sx={{ textTransform: 'none' }}
                >
                  Members
                </Button>
              </Tooltip>
            )}
          </Box>

          {/* Messages area */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              px: { xs: 2, sm: 3 },
              py: 2,
              bgcolor: '#fafafa',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Load more */}
            {hasMore && (
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Button size="small" onClick={handleLoadMore}>
                  Load older messages
                </Button>
              </Box>
            )}

            {messagesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress size={32} />
              </Box>
            ) : messages.length === 0 ? (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    {activeChannel.type === 'dm'
                      ? `Start a conversation with ${activeDisplayName}`
                      : `Welcome to #${activeDisplayName}`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    This is the beginning of this conversation.
                  </Typography>
                </Box>
              </Box>
            ) : (
              <>
                {messages.map((msg, i) => (
                  <MessageBubble
                    key={msg.id}
                    message={msg}
                    prevMessage={messages[i - 1]}
                    currentUserId={currentUser?.id}
                  />
                ))}
              </>
            )}
            <div ref={messagesEndRef} />
          </Box>

          {/* Typing indicator + compose */}
          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              py: 1.5,
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            {typingLabel && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontStyle: 'italic' }}>
                {typingLabel}
              </Typography>
            )}
            <Paper
              variant="outlined"
              sx={{
                display: 'flex',
                alignItems: 'flex-end',
                gap: 1,
                px: 1.5,
                py: 0.75,
                borderRadius: 2,
              }}
            >
              <InputBase
                fullWidth
                multiline
                maxRows={6}
                inputRef={composeRef}
                placeholder={`Message ${activeDisplayName}…`}
                value={compose}
                onChange={handleComposeChange}
                onKeyDown={handleComposeKeyDown}
                sx={{ fontSize: '0.9rem' }}
              />
              <IconButton
                color="primary"
                onClick={handleSend}
                disabled={!compose.trim() || sending}
                size="small"
                sx={{ mb: 0.25 }}
              >
                {sending ? <CircularProgress size={18} /> : <SendIcon />}
              </IconButton>
            </Paper>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Enter to send · Shift+Enter for new line
            </Typography>
          </Box>
        </>
      ) : (
        /* Empty state */
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fafafa' }}>
          <Box sx={{ textAlign: 'center', maxWidth: 320 }}>
            <Typography variant="h5" fontWeight={700} gutterBottom color="text.secondary">
              ACS Messages
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select a channel from the sidebar or start a new conversation.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setNewChannelOpen(true)}
            >
              New Message
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        height: 'calc(100vh - 64px)',
        mx: -3,
        mt: -3,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {/* Desktop: always show sidebar */}
      <Box sx={{ display: { xs: 'none', sm: 'flex' }, height: '100%' }}>
        {sidebar}
      </Box>

      {/* Mobile: toggle sidebar / main */}
      <Box sx={{ display: { xs: 'flex', sm: 'none' }, width: '100%', height: '100%' }}>
        {mobileSidebarOpen ? sidebar : mainPanel}
      </Box>

      {/* Desktop main panel */}
      <Box sx={{ display: { xs: 'none', sm: 'flex' }, flex: 1, height: '100%', minWidth: 0 }}>
        {mainPanel}
      </Box>

      {/* New Channel / DM Modal */}
      <NewChannelModal
        open={newChannelOpen}
        onClose={() => setNewChannelOpen(false)}
        onChannelCreated={handleChannelCreated}
      />

      {/* Members panel — add/remove members on a channel.
          Tom Clancy, 2026-04-25: Option C — anyone in the channel can add
          or remove anyone in the company. "auto" badge marks members the
          system added based on their role on the project. */}
      <Dialog
        open={membersOpen}
        onClose={() => setMembersOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <PeopleIcon fontSize="small" />
          <span>Members of {activeChannel?.name || 'this channel'}</span>
          <Box sx={{ flex: 1 }} />
          <IconButton size="small" onClick={() => setMembersOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ px: 2, py: 1.5 }}>
          {/* Add a new member */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
            <Autocomplete
              size="small"
              fullWidth
              options={(allUsers || []).filter(u => !members.some(m => m.user_id === u.id))}
              getOptionLabel={(o) => o?.name || ''}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              value={addPickerValue}
              onChange={(_, v) => setAddPickerValue(v)}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem' }}>
                      {option.name?.charAt(0)}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>{option.name}</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {option.role || option.email}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Add someone…"
                  size="small"
                />
              )}
            />
            <Button
              variant="contained"
              size="small"
              startIcon={<PersonAddIcon />}
              onClick={handleAddMember}
              disabled={!addPickerValue || memberMutating}
              sx={{ textTransform: 'none' }}
            >
              Add
            </Button>
          </Box>

          {/* Member list */}
          {membersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : members.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No members yet.
            </Typography>
          ) : (
            <List dense disablePadding>
              {members.map((m) => {
                const isAuto = m.membership_source === 'auto';
                const isSelf = m.user_id === currentUser?.id;
                return (
                  <ListItem
                    key={m.user_id}
                    secondaryAction={
                      <Tooltip title={isSelf ? 'Leave this channel' : 'Remove from channel'}>
                        <span>
                          <IconButton
                            size="small"
                            edge="end"
                            onClick={() => handleRemoveMember(m.user_id)}
                            disabled={memberMutating}
                          >
                            <PersonRemoveIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    }
                    sx={{ pl: 0 }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ width: 32, height: 32, fontSize: '0.85rem' }}>
                        {m.name?.charAt(0)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {m.name}{isSelf ? ' (you)' : ''}
                          </Typography>
                          {isAuto && (
                            <Tooltip title="Added automatically based on their role on this project">
                              <Chip
                                size="small"
                                label="auto"
                                icon={<AutoBadgeIcon sx={{ fontSize: 12 }} />}
                                sx={{ height: 18, fontSize: '0.65rem', bgcolor: alpha('#2e7d32', 0.1), color: '#2e7d32' }}
                              />
                            </Tooltip>
                          )}
                          {m.role === 'admin' && (
                            <Chip size="small" label="admin" sx={{ height: 18, fontSize: '0.65rem' }} />
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {m.user_role || m.email}
                        </Typography>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.25 }}>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            {members.length} member{members.length === 1 ? '' : 's'} • anyone here can add or remove
          </Typography>
          <Button onClick={() => setMembersOpen(false)} size="small">Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
